import { collection, getDocs, query, where } from 'firebase/firestore';

import { NIVELES_EVALUACION, crearClaveProgreso } from '../config/evaluacionesCatalogo';
import { db } from '../firebase/firebase';

const normalizarTexto = (valor = '') => valor
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const normalizarNivel = (valor = '') => {
  const nivel = normalizarTexto(valor);
  if (nivel.includes('basico')) return 'basico';
  if (nivel.includes('intermedio')) return 'intermedio';
  if (nivel.includes('avanzado')) return 'avanzado';
  return '';
};

const obtenerNumeroEjercicio = (nombre = '') => {
  const coincidencias = [...nombre.toString().matchAll(/ejercicio\s+(\d+)/gi)];
  return coincidencias.length ? Number(coincidencias.at(-1)[1]) : null;
};

const obtenerNumeroVisible = (ejercicio) => obtenerNumeroEjercicio(ejercicio.nombre);

const resolverSinNumero = (nivel, modulo, nombre, ejercicios) => {
  if (ejercicios.length === 1) return ejercicios[0];

  const texto = normalizarTexto(nombre);
  if (nivel === 'basico' && modulo === 'Modulo1' && texto.includes('comprension')) return ejercicios[0];
  if (nivel === 'basico' && modulo === 'Modulo3' && texto.includes('comprension')) return ejercicios[1];
  if (nivel === 'basico' && modulo === 'Modulo4' && texto.includes('vocabulario')) return ejercicios[0];
  if (texto.includes('retencion')) return ejercicios.find((item) => normalizarTexto(item.nombre).includes('retencion'));
  if (texto.includes('comprension')) return ejercicios.find((item) => normalizarTexto(item.nombre).includes('comprension'));
  if (texto.includes('elaboracion de cuadros')) return ejercicios.find((item) => normalizarTexto(item.nombre).includes('elaboracion'));
  if (texto.includes('metodo de lectura')) return ejercicios.find((item) => normalizarTexto(item.nombre).includes('metodo de lectura'));
  return null;
};

export const resolverEvaluacionHistorica = (datos = {}) => {
  const nivel = normalizarNivel(datos.nivel);
  const numeroModulo = datos.modulo?.toString().match(/\d+/)?.[0];
  const modulo = numeroModulo ? `Modulo${numeroModulo}` : '';
  const ejercicios = NIVELES_EVALUACION[nivel]?.modulos?.[modulo];
  if (!ejercicios?.length) return null;

  const numeroEjercicio = obtenerNumeroEjercicio(datos.nombre || datos.ejercicio);
  const ejercicio = numeroEjercicio !== null
    ? ejercicios.find((item) => obtenerNumeroVisible(item) === numeroEjercicio)
    : resolverSinNumero(nivel, modulo, datos.nombre || datos.ejercicio, ejercicios);

  if (!ejercicio) return null;
  return {
    nivel,
    modulo,
    ejercicio,
    clave: crearClaveProgreso(nivel, modulo, ejercicio.id),
  };
};

const esEntregaFinal = (clave, datos) => {
  if (clave === 'basico_modulo4_8') return Boolean(datos.respuestaLibre?.toString().trim());
  if (clave === 'intermedio_modulo6_2') return normalizarTexto(datos.tipo) === 'respuesta libre';
  return true;
};

const obtenerMilisegundos = (fecha) => {
  if (fecha?.toMillis) return fecha.toMillis();
  if (fecha instanceof Date) return fecha.getTime();
  const valor = fecha ? new Date(fecha).getTime() : 0;
  return Number.isNaN(valor) ? 0 : valor;
};

export const construirProgresoHistorico = (documentos = []) => documentos.reduce((resultado, documento) => {
  const datos = documento.data ? documento.data() : documento;
  const id = documento.id || datos.id;
  const resuelta = resolverEvaluacionHistorica(datos);
  if (!resuelta) return resultado;

  const anterior = resultado[resuelta.clave];
  const estadoDocumento = esEntregaFinal(resuelta.clave, datos) ? 'completado' : 'en-curso';
  const estado = anterior?.estado === 'completado' ? 'completado' : estadoDocumento;
  const fechaDocumento = datos.createdAt || datos.fecha || null;
  const fechaAnterior = anterior?.actualizadoEn || null;
  const actualizadoEn = obtenerMilisegundos(fechaDocumento) >= obtenerMilisegundos(fechaAnterior)
    ? fechaDocumento
    : fechaAnterior;

  resultado[resuelta.clave] = {
    ...anterior,
    clave: resuelta.clave,
    nivel: resuelta.nivel,
    modulo: resuelta.modulo,
    ejercicioId: resuelta.ejercicio.id,
    nombre: resuelta.ejercicio.nombre,
    estado,
    porcentaje: estado === 'completado' ? 100 : 0,
    historico: true,
    origen: 'evaluaciones',
    evaluacionHistoricaIds: [...new Set([...(anterior?.evaluacionHistoricaIds || []), id].filter(Boolean))],
    iniciadoEn: anterior?.iniciadoEn || fechaDocumento,
    actualizadoEn,
    ultimoAccesoEn: actualizadoEn,
    ...(estado === 'completado' && { completadoEn: anterior?.completadoEn || fechaDocumento }),
  };

  return resultado;
}, {});

export const combinarProgresoEvaluaciones = (actual = {}, historico = {}) => {
  const combinado = { ...actual };

  Object.entries(historico).forEach(([clave, registroHistorico]) => {
    const registroActual = combinado[clave];
    if (!registroActual) {
      combinado[clave] = registroHistorico;
      return;
    }

    if (registroActual.estado !== 'completado' && registroHistorico.estado === 'completado') {
      combinado[clave] = {
        ...registroActual,
        ...registroHistorico,
        intentos: registroActual.intentos || 0,
      };
    }
  });

  return combinado;
};

export const obtenerCambiosProgreso = (actual = {}, combinado = {}) => Object.fromEntries(
  Object.entries(combinado).filter(([clave, registro]) => {
    const previo = actual[clave];
    return !previo || (previo.estado !== 'completado' && registro.estado === 'completado');
  })
);

export const cargarProgresoHistorico = async (userId) => {
  if (!db || !userId) return {};
  const snapshot = await getDocs(query(collection(db, 'evaluaciones'), where('userId', '==', userId)));
  return construirProgresoHistorico(snapshot.docs);
};
